import Alpine from 'alpinejs';
import axios from '../../config/axios.js';

const toast = (msg, type = 'info') => window.toast?.(msg, type);

function createDashboardBio() {
  return {
    loading: true,
    saving: false,
    page: null,
    themes: [],
    features: {},
    level: 0,
    levelName: '',
    publicUrl: '',
    linkForm: { title: '', url: '', icon: '', is_highlighted: false },
    editingLinkId: null,
    showLinkForm: false,

    async init() {
      await this.fetch();
    },

    async fetch() {
      this.loading = true;
      try {
        const { data } = await axios.get('/api/v1/bio');
        const d = data.data || {};
        this.page = d.page;
        this.themes = d.themes || [];
        this.features = d.features || {};
        this.level = d.level ?? 0;
        this.levelName = d.level_name || '';
        this.publicUrl = d.public_url || this.page?.public_url || '';
      } catch (e) {
        toast(e.response?.data?.message || 'Gagal memuat bio link.', 'error');
      } finally {
        this.loading = false;
      }
    },

    get accentPalette() {
      if (this.features.allow_custom_accent) return null;
      return this.features.accent_palette || this.features.accent_colors || [];
    },

    previewClasses() {
      const p = this.page || {};
      return [
        'bio-page',
        `bio-page--theme-${p.theme || 'minimal'}`,
        `bio-page--bg-${p.bg_style || 'solid'}`,
        `bio-page--btn-${p.button_style || 'rounded'}`,
        `bio-page--font-${p.font_style || 'sans'}`,
      ].join(' ');
    },

    activeLinks() {
      return (this.page?.links || []).filter((l) => l.is_active !== false);
    },

    async savePage(extra = {}) {
      if (!this.page) return;
      this.saving = true;
      try {
        const payload = {
          slug: this.page.slug,
          title: this.page.title,
          headline: this.page.headline,
          bio: this.page.bio,
          theme: this.page.theme,
          accent_color: this.page.accent_color,
          bg_style: this.page.bg_style,
          button_style: this.page.button_style,
          font_style: this.page.font_style,
          ...extra,
        };
        const { data } = await axios.put('/api/v1/bio', payload);
        const d = data.data || {};
        this.page = d.page;
        this.themes = d.themes || this.themes;
        this.features = d.features || this.features;
        this.publicUrl = d.public_url || this.publicUrl;
        toast(data.message || 'Tersimpan.', 'success');
      } catch (e) {
        toast(e.response?.data?.message || 'Gagal menyimpan.', 'error');
      } finally {
        this.saving = false;
      }
    },

    async selectTheme(theme) {
      if (!theme?.unlocked) {
        toast(`Tema terkunci. Butuh Level ${theme.min_level}.`, 'error');
        return;
      }
      this.page.theme = theme.code;
      if (theme.defaults) {
        this.page.accent_color = theme.defaults.accent || this.page.accent_color;
        if ((this.features.bg_styles || []).includes(theme.defaults.bg)) {
          this.page.bg_style = theme.defaults.bg;
        }
        if ((this.features.button_styles || []).includes(theme.defaults.button)) {
          this.page.button_style = theme.defaults.button;
        }
        if ((this.features.font_styles || []).includes(theme.defaults.font)) {
          this.page.font_style = theme.defaults.font;
        }
      }
      await this.savePage();
    },

    async setAccent(color) {
      this.page.accent_color = color;
      await this.savePage();
    },

    async togglePublish() {
      this.saving = true;
      try {
        const { data } = await axios.post('/api/v1/bio/publish', {
          is_published: !this.page.is_published,
        });
        const d = data.data || {};
        this.page = d.page;
        toast(data.message || 'Status publish diperbarui.', 'success');
      } catch (e) {
        toast(e.response?.data?.message || 'Gagal mengubah status.', 'error');
      } finally {
        this.saving = false;
      }
    },

    async onAvatar(e) {
      const file = e.target.files?.[0];
      if (!file) return;
      const fd = new FormData();
      fd.append('avatar', file);
      try {
        const { data } = await axios.post('/api/v1/bio/avatar', fd);
        this.page = { ...this.page, ...data.data };
        toast('Avatar diunggah.', 'success');
      } catch (err) {
        toast(err.response?.data?.message || 'Gagal upload avatar.', 'error');
      }
      e.target.value = '';
    },

    async onCover(e) {
      const file = e.target.files?.[0];
      if (!file) return;
      const fd = new FormData();
      fd.append('cover', file);
      try {
        const { data } = await axios.post('/api/v1/bio/cover', fd);
        this.page = { ...this.page, ...data.data };
        toast('Cover diunggah.', 'success');
      } catch (err) {
        toast(err.response?.data?.message || 'Gagal upload cover.', 'error');
      }
      e.target.value = '';
    },

    openAddLink() {
      this.editingLinkId = null;
      this.linkForm = { title: '', url: '', icon: '', is_highlighted: false };
      this.showLinkForm = true;
    },

    openEditLink(link) {
      this.editingLinkId = link.id;
      this.linkForm = {
        title: link.title,
        url: link.url,
        icon: link.icon || '',
        is_highlighted: !!link.is_highlighted,
      };
      this.showLinkForm = true;
    },

    async saveLink() {
      if (!this.linkForm.title?.trim() || !this.linkForm.url?.trim()) {
        toast('Judul dan URL wajib.', 'error');
        return;
      }
      this.saving = true;
      try {
        if (this.editingLinkId) {
          await axios.put(`/api/v1/bio/links/${this.editingLinkId}`, this.linkForm);
        } else {
          await axios.post('/api/v1/bio/links', this.linkForm);
        }
        this.showLinkForm = false;
        await this.fetch();
        toast('Link disimpan.', 'success');
      } catch (e) {
        toast(e.response?.data?.message || 'Gagal menyimpan link.', 'error');
      } finally {
        this.saving = false;
      }
    },

    async removeLink(link) {
      const ok = await window.confirmDialog?.({
        title: 'Hapus link?',
        message: `Hapus "${link.title}"?`,
        confirmText: 'Hapus',
        variant: 'danger',
      });
      if (ok === false) return;
      try {
        await axios.delete(`/api/v1/bio/links/${link.id}`);
        await this.fetch();
        toast('Link dihapus.', 'success');
      } catch (e) {
        toast(e.response?.data?.message || 'Gagal menghapus.', 'error');
      }
    },

    async moveLink(link, dir) {
      const links = [...(this.page.links || [])];
      const idx = links.findIndex((l) => l.id === link.id);
      const swap = idx + dir;
      if (idx < 0 || swap < 0 || swap >= links.length) return;
      const tmp = links[idx];
      links[idx] = links[swap];
      links[swap] = tmp;
      try {
        const { data } = await axios.post('/api/v1/bio/links/reorder', {
          ids: links.map((l) => l.id),
        });
        this.page = data.data?.page || { ...this.page, links };
      } catch (e) {
        toast(e.response?.data?.message || 'Gagal mengubah urutan.', 'error');
      }
    },

    copyUrl() {
      const url = this.publicUrl || this.page?.public_url;
      if (!url) return;
      navigator.clipboard?.writeText(url);
      toast('URL disalin.', 'success');
    },
  };
}

Alpine.data('dashboardBio', createDashboardBio);
